import React from "react";
import { motion } from "framer-motion";
import { Camera, Tag, MessageCircle, BarChart, Sparkles, Zap } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

export default function TipsPage() {
  const tips = [
    {
      title: "Take High-Quality Photos",
      description: "Use good lighting and show the item from multiple angles. Clear photos build trust and attract more buyers.",
      icon: Camera
    },
    {
      title: "Write Detailed Descriptions",
      description: "Include all relevant details like condition, size, brand, and any flaws. Honesty prevents disputes.",
      icon: Sparkles
    },
    {
      title: "Price Fairly",
      description: "Research similar items on HuluOrder to set a competitive price. Be open to slight negotiation.",
      icon: Tag
    },
    {
      title: "Respond Quickly",
      description: "Buyers often message multiple sellers. A fast response time significantly increases your chance of a sale.",
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-gray-50 text-center">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-green-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400"
            >
              Selling Tips
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-gray-400 max-w-2xl mx-auto"
            >
              Master the art of selling on HuluOrder. Follow these expert tips 
              to sell your items faster and more efficiently.
            </motion.p>
          </div>
        </section>

        {/* Tips Grid */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {tips.map((tip, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100 hover:shadow-xl transition-all group"
              >
                <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                  <tip.icon className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{tip.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {tip.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Pro Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-gray-900 rounded-[3rem] p-12 lg:p-20 text-white flex flex-col lg:flex-row items-center gap-16">
              <div className="flex-1 space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-bold uppercase tracking-wider">
                  <BarChart className="w-4 h-4" /> Pro Feature
                </div>
                <h2 className="text-4xl font-bold leading-tight">Boost Your Post</h2>
                <p className="text-gray-400 text-lg">
                  Want to sell even faster? Boosting your item puts it at the top of 
                  search results and gives it premium visibility in your category.
                </p>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-green-400" />
                    <span className="text-sm font-medium">3x More Views</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-green-400" />
                    <span className="text-sm font-medium">Faster Sales</span>
                  </div>
                </div>
              </div>
              <div className="flex-1 bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
                <div className="space-y-4">
                  {[1, 2, 3].map((_, i) => (
                    <div key={i} className="flex gap-4 items-center">
                      <div className="w-16 h-16 bg-white/10 rounded-xl"></div>
                      <div className="flex-1 space-y-2">
                        <div className="w-2/3 h-2 bg-white/20 rounded"></div>
                        <div className="w-1/3 h-2 bg-white/10 rounded"></div>
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-white/10 text-center">
                    <p className="text-green-400 font-bold">Coming Soon!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
